import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "../../../theme/customTheme";

export const useStyles = makeStyles({
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    height: "100%",
  },
  messageContainer: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    gap: designTokens.spacing[5],
    backgroundColor: designTokens.neutral[50],
    width: "100%",
    maxWidth: "800px",
    flex: 1,
    paddingLeft: designTokens.spacing[5],
    paddingRight: designTokens.spacing[5],
    boxSizing: "border-box",
    [`@media (max-width: ${designTokens.breakpoint["2xl"]})`]: {
      textWrap: "wrap",
      wordBreak: "break-word",
      flexWrap: "wrap",
      width: "100%",
    },
  },
  messageWrapper: {
    width: "100%",
    display: "flex",
    alignContent: "center",
    flexDirection: "column",
    flex: 1,
  },
  noMessagesContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    flex: 1,
  },
  noMessagesText: {
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.normal,
    textAlign: "center",
    lineHeight: designTokens.spacing[6],
  },
  messageContent: {
    position: "relative",
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[5]}`,
    boxSizing: "border-box",
    wordBreak: "break-word",
    "& code": {
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      maxWidth: "100%",
      display: "block",
      backgroundColor: designTokens.neutral.white,
      padding: designTokens.spacing[2],
      borderRadius: designTokens.borderRadius.sm,
      overflowX: "auto",
    },
    "& pre": {
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      maxWidth: "100%",
      backgroundColor: designTokens.neutral.white,
      padding: designTokens.spacing[2],
      borderRadius: designTokens.borderRadius.sm,
      overflowX: "auto",
    },
  },
  messageDetails: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    gap: designTokens.spacing[2],
    width: "100%",
  },
  assistantMessage: {
    alignItems: "start",
  },
  userMessage: {
    alignItems: "end",
  },
  editInput: {
    width: "60%",
  },
  floatingRewriteButton: {
    backgroundColor: designTokens.accent[500],
    position: "fixed",
    zIndex: 100,
    height: designTokens.spacing[6],
    width: designTokens.spacing[12],
  },
  tagWrapper: {
    display: "flex",
    gap: designTokens.spacing[1],
    flexWrap: "wrap",
  },
  fileCard: {
    height: "fit-content",
  },
  dateStyle: {
    padding: `0 0 0 ${designTokens.spacing[1]}`,
    color: designTokens.neutral[500],
  },
  actionsWrapper: {
    display: "flex",
    gap: designTokens.spacing[1],
  },
  captionWrapper: {
    textAlign: "end",
  },
  refreshingOverlay: {
    filter: "blur(2px)",
    opacity: 0.4,
    pointerEvents: "none",
    transition: `all ${designTokens.transition.slow}`,
  },
  highlightedCitation: {
    backgroundColor: designTokens.semantic.warningLight,
    padding: `0 ${designTokens.spacing[1]}`,
    borderRadius: designTokens.borderRadius.sm,
    cursor: "pointer",
    transition: `background-color ${designTokens.transition.slow}`,
    ":hover": {
      backgroundColor: designTokens.semantic.warning,
    },
  },
  webCitationCard: {
    marginTop: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    borderLeft: `4px solid ${designTokens.accent[500]}`,
    backgroundColor: designTokens.neutral[100],
    borderRadius: designTokens.borderRadius.sm,
    wordBreak: "break-word",
    width: "100%",
    boxSizing: "border-box",
    "& a": {
      color: designTokens.accent[500],
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  webCitationSectionTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[600],
    padding: `${designTokens.spacing[3]} 0 ${designTokens.spacing[1]} 0`,
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
    marginBottom: designTokens.spacing[3],
  },
  referenceSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[1],
  },
  referenceSectionGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
  },
  referenceTag: {
    backgroundColor: designTokens.accent[500],
    color: designTokens.neutral.white,
    cursor: "default",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "500px",
  },
  stickyHeader: {
    position: "sticky",
    top: "0",
    zIndex: 100,
    backgroundColor: designTokens.neutral[50],
  },
  refreshingSpinner: {
    display: "flex",
    backgroundColor: designTokens.neutral[50],
    justifyContent: "center",
    padding: designTokens.spacing[6],
  },
  chartWrapper: {
    marginTop: designTokens.spacing[5],
  },
  messageContentMobile: {
    fontSize: designTokens.fontSize.xs,
  },
  mobileFullWidth: {
    width: "100%",
  },
  tagActionsRow: {
    display: "flex",
    gap: designTokens.spacing[2],
    alignItems: "center",
    flexWrap: "wrap",
  },
  actionButtonsGroup: {
    display: "flex",
    gap: designTokens.spacing[1],
    alignItems: "center",
  },
  fileCardHeader: {
    maxWidth: "340px",
  },
  cardHeaderImage: {
    width: designTokens.spacing[10],
    height: designTokens.spacing[10],
  },
  fileLinkTruncated: {
    cursor: "pointer",
    display: "inline-block",
    maxWidth: "200px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  sectionWrapper: {
    marginTop: designTokens.spacing[6],
  },
  sectionTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[600],
    marginBottom: designTokens.spacing[2],
  },
  sourceDocCard: {
    width: "100%",
  },
  cursorPointer: {
    cursor: "pointer",
  },
  citationIdText: {
    marginLeft: designTokens.spacing[2],
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[600],
  },
  sourceDocContent: {
    textAlign: "start",
    color: designTokens.neutral[600],
  },
  referenceSectionMargin: {
    marginTop: designTokens.spacing[3],
  },
  guidelinesLink: {
    color: designTokens.accent[500],
    textDecoration: "underline",
  },
  templateButtonsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[3],
    marginTop: designTokens.spacing[5],
    justifyContent: "center",
    maxWidth: "800px",
  },
  templateButton: {
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[4]}`,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  templateButtonIcon: {
    fontSize: designTokens.fontSize.base,
  },
  templateButtonText: {
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.normal,
  },
  galleryIconButton: {
    minWidth: "auto",
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    color: designTokens.neutral[500],
  },
  noMessagesContainerHidden: {
    opacity: 0,
  },
  noMessagesContainerVisible: {
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: "0.3s",
    animationTimingFunction: "ease-in",
    animationFillMode: "forwards",
  },
});
